import { AsyncPipe, CurrencyPipe, DecimalPipe } from '@angular/common';
import { Component, inject, model, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageService } from 'primeng/api';
import { TradeStudyInputsService } from '../../services/trade-study-inputs.service';
import type { TradeStudyInputsUpdate } from '../../../models/trade-study-inputs.model';
import { SettingsService } from '../../settings-page/settings-service/settings.service';

@Component({
  selector: 'app-trade-study-layout-dialog',
  imports: [
    AsyncPipe,
    CurrencyPipe,
    DecimalPipe,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputNumberModule,
  ],
  templateUrl: './trade-study-layout-dialog.component.html',
  styleUrl: './trade-study-layout-dialog.component.scss',
})
export class TradeStudyLayoutDialogComponent {
  private tradeStudyInputsService = inject(TradeStudyInputsService);
  private settingsService = inject(SettingsService);
  private messageService = inject(MessageService);

  visible = model(false);
  quoteId = input.required<string>();
  saved = output<void>();
  currencyCode$ = this.settingsService.settings$.pipe(
    map((settings) => settings?.organisation_base_currency_code || 'GBP'),
  );

  loading = false;
  saving = false;

  total_assemblies_required = 0;
  parts_per_assembly = 0;
  program_duration_years = 0;
  labor_rate_per_hour = 0;
  overhead_percent = 0;
  labor_utilization = 0;
  ot_1_shift_percent = 0;
  ot_2_shifts_percent = 0;
  hours_day_1_shift = 8;
  hours_day_2_shifts = 16;
  hours_day_3_shifts = 24;
  hours_available_for_ac = 21;
  layout_shifts = 1;
  layout_hours_per_shift = 8;
  layout_days_per_year = 250;
  layout_equipment_efficiency = 0.8;

  get mDaysPerMonth(): number {
    if (!this.layout_days_per_year) return 1;
    return Math.max(1, Math.floor(this.layout_days_per_year / 12));
  }

  get costPerHeadPerHour(): number {
    return this.labor_rate_per_hour * (1 + this.overhead_percent / 100);
  }

  private get programMonths(): number {
    const m = this.program_duration_years * 12;
    return Number.isFinite(m) && m > 0 ? m : 0;
  }

  get assembliesPerMonth(): number {
    if (!this.programMonths) return 0;
    return this.total_assemblies_required / this.programMonths;
  }

  get partsPerMonth(): number {
    return this.assembliesPerMonth * this.parts_per_assembly;
  }

  get assembliesPerDay(): number {
    if (!this.mDaysPerMonth) return 0;
    return this.assembliesPerMonth / this.mDaysPerMonth;
  }

  get partsPerDay(): number {
    if (!this.mDaysPerMonth) return 0;
    return this.partsPerMonth / this.mDaysPerMonth;
  }

  get overheadRate(): number {
    return this.labor_rate_per_hour * (this.overhead_percent / 100);
  }

  get hoursDay1ShiftPlusOt(): number {
    return this.layout_hours_per_shift * (1 + this.ot_1_shift_percent / 100);
  }

  get hoursDay2ShiftPlusOt(): number {
    return (
      this.layout_hours_per_shift * 2 * (1 + this.ot_2_shifts_percent / 100)
    );
  }

  get hoursDay3ShiftsPlusOt(): number {
    return (
      this.layout_hours_per_shift * 3 * (1 + this.ot_2_shifts_percent / 100)
    );
  }

  get usableHours1Shift(): number {
    return this.hoursDay1ShiftPlusOt * this.labor_utilization;
  }

  get usableHours2Shifts(): number {
    return this.hoursDay2ShiftPlusOt * this.labor_utilization;
  }

  get usableHours3Shifts(): number {
    return this.hoursDay3ShiftsPlusOt * this.labor_utilization;
  }

  async onDialogShow(): Promise<void> {
    this.loading = true;
    try {
      const data = await this.tradeStudyInputsService.getTradeStudyInputs(
        this.quoteId(),
      );
      this.total_assemblies_required = data.total_assemblies_required;
      this.parts_per_assembly = data.parts_per_assembly;
      this.program_duration_years = data.program_duration_years;
      this.layout_shifts = data.layout_shifts;
      this.layout_hours_per_shift = data.layout_hours_per_shift;
      this.layout_days_per_year = data.layout_days_per_year;
      this.layout_equipment_efficiency = data.layout_equipment_efficiency;
      this.labor_rate_per_hour = data.labor_rate_per_hour;
      this.overhead_percent = data.overhead_percent;
      this.labor_utilization = data.labor_utilization;
      this.ot_1_shift_percent = data.ot_1_shift_percent;
      this.ot_2_shifts_percent = data.ot_2_shifts_percent;
      this.hours_day_1_shift = data.hours_day_1_shift;
      this.hours_day_2_shifts = data.hours_day_2_shifts;
      this.hours_day_3_shifts = data.hours_day_3_shifts;
      this.hours_available_for_ac = data.hours_available_for_ac;
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Load failed',
        detail: 'Could not load trade study inputs.',
      });
    } finally {
      this.loading = false;
    }
  }

  close(): void {
    this.visible.set(false);
  }

  private payload(): TradeStudyInputsUpdate {
    return {
      total_assemblies_required: this.total_assemblies_required,
      parts_per_assembly: this.parts_per_assembly,
      program_duration_years: this.program_duration_years,
      layout_shifts: this.layout_shifts,
      layout_hours_per_shift: this.layout_hours_per_shift,
      layout_days_per_year: this.layout_days_per_year,
      layout_equipment_efficiency: this.layout_equipment_efficiency,
      labor_rate_per_hour: this.labor_rate_per_hour,
      overhead_percent: this.overhead_percent,
      labor_utilization: this.labor_utilization,
      ot_1_shift_percent: this.ot_1_shift_percent,
      ot_2_shifts_percent: this.ot_2_shifts_percent,
      hours_available_for_ac: this.hours_available_for_ac,
    };
  }

  async save(): Promise<void> {
    this.saving = true;
    try {
      await this.tradeStudyInputsService.updateTradeStudyInputs(
        this.quoteId(),
        this.payload(),
      );
      this.saved.emit();
      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: 'Trade study inputs were updated.',
      });
      this.visible.set(false);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Save failed',
        detail: 'Could not save trade study inputs.',
      });
    } finally {
      this.saving = false;
    }
  }
}
